import { observer } from "mobx-react-lite";
import type React from "react";
import {
  StyledModalContainer,
  StyledModalHeader,
  StyledOverlay,
} from "./ModalStyles";
import { t } from "i18next";
import { useRootStore } from "../../stores/hooks";
import {
  EmptyButton,
  ModalFooter,
  RoundButton,
  TextSizes,
  Tooltip,
} from "@smartsuite/react-ui/lib";

interface ModalProps {
  header: React.ReactNode;
  children: React.ReactNode;
  footerLine?: React.ReactNode;
  title: string;
  positiveButtonText?: string;
  handleClose?: () => void;
  handleNegativeButton?: () => void;
  handlePositiveButton?: () => void;
  isDisabledPositive?: boolean;
  isDisabledNegative?: boolean;
  isLoading?: boolean;
  tooltipText?: string;
}

const roundButtonStyles = {
  height: 32,
  sidePadding: 15,
  textSize: TextSizes.L,
};

const Modal = observer((props: ModalProps): React.ReactElement => {
  const {
    header,
    footerLine = null,
    handleClose,
    children,
    handleNegativeButton,
    handlePositiveButton,
    positiveButtonText,
    isDisabledPositive,
    isDisabledNegative,
    isLoading,
    tooltipText,
  } = props;

  const isAllowCloseByClickOutside = !isDisabledPositive;

  const store = useRootStore();

  const isShowFooter =
    handleNegativeButton !== undefined || handlePositiveButton !== undefined;

  const footerButtons = (
    <>
      <EmptyButton
        size="lg"
        isDisabled={isDisabledNegative}
        style={{ marginRight: 10 }}
        onClick={handleNegativeButton}
      >
        {t("general.cancel")}
      </EmptyButton>

      {!store.automations.candidate.showErrorSection && (
        <Tooltip
          text={tooltipText}
          target={
            <RoundButton
              styles={roundButtonStyles}
              disabled={!isLoading && isDisabledPositive}
              loading={isLoading}
              onClick={handlePositiveButton}
            >
              {positiveButtonText}
            </RoundButton>
          }
        />
      )}
    </>
  );

  return (
    <>
      <StyledOverlay
        onClick={isAllowCloseByClickOutside ? handleClose : undefined}
      ></StyledOverlay>

      <StyledModalContainer>
        <StyledModalHeader>{header}</StyledModalHeader>

        {children}

        {isShowFooter && (
          <ModalFooter actions={footerLine} buttons={footerButtons} />
        )}
      </StyledModalContainer>
    </>
  );
});

export default Modal;
